import type { QueueCheckPerson, QueueRow } from '@myrewards/contracts';
import type { ReactElement } from 'react';
import { Link } from 'react-aria-components';
import {
  cx,
  DataTable,
  formatCount,
  formatMoney,
  plural,
  StatusPill,
  type DataColumn,
} from '../../ui';
import { paidFrom } from '../check/presenter';
import { amountWords, costTypeWord, TYPE_LABEL } from '../labels';
import { rowHref } from './presenter';
import s from '../Ops.module.css';

type Check = NonNullable<QueueRow['check']>;

/** "Not coded yet" until the line has its code; codes stay text (15125.110 keeps its zero). */
function code(value: string | undefined): ReactElement {
  return value === undefined ? (
    <span className={s.muted}>Not coded yet</span>
  ) : (
    <span className={s.code}>{value}</span>
  );
}

function columns(row: QueueRow, check: Check): DataColumn<QueueCheckPerson>[] {
  const withOptions = check.people.some((p) => p.options > 0);
  // As the check's Coding says it: a PAA or TAA project comes from its pool (SA2).
  const noProject = `None for ${row.type === 'PAA' || row.type === 'TAA' ? 'this pool' : 'this award'}`;
  return [
    {
      id: 'person',
      header: 'Person',
      card: 'title',
      cell: (p) => <span className={s.strong}>{p.person.name}</span>,
    },
    { id: 'cash', header: 'Cash', numeric: true, cell: (p) => formatMoney(p.cashCents) },
    ...(withOptions
      ? [
          {
            id: 'options',
            header: 'Options',
            numeric: true,
            cell: (p: QueueCheckPerson) => (p.options === 0 ? 'Cash only' : formatCount(p.options)),
          },
        ]
      : []),
    { id: 'pool', header: 'Paid from', cell: (p) => p.pool.name },
    { id: 'cost', header: 'Cost type', cell: (p) => costTypeWord(p.costType) },
    { id: 'account', header: 'Account', cell: (p) => code(p.coding?.account) },
    { id: 'org', header: 'Org', cell: (p) => code(p.coding?.org) },
    {
      id: 'project',
      header: 'Project',
      cell: (p) =>
        p.coding === null ? (
          code(undefined)
        ) : p.coding.project === null ? (
          <span className={s.muted}>{noProject}</span>
        ) : (
          code(p.coding.project)
        ),
    },
  ];
}

/** The award's proof or write-up: there, still needed, or none needed for its type. */
function ProofFact({ row, check }: { row: QueueRow; check: Check }): ReactElement {
  const { needed, present } = check.proof;
  if (needed === null) {
    const typeName = TYPE_LABEL[row.type];
    return (
      <div className={s.rowFact}>
        <p className={cx('eyebrow', s.fieldLabel)}>Proof</p>
        <p className={s.strong}>None needed</p>
        <p className={s.sub}>
          {/^[AEIOU]/.test(typeName) ? 'An' : 'A'} {typeName} needs no approval proof.
        </p>
      </div>
    );
  }
  const what = needed === 'proof' ? 'Approval proof' : 'Write-up';
  return (
    <div className={s.rowFact}>
      <p className={cx('eyebrow', s.fieldLabel)}>{what}</p>
      <StatusPill
        size="sm"
        tone={present ? 'done' : 'waiting'}
        label={present ? (needed === 'proof' ? 'Attached' : 'On the award') : 'Still needed'}
      />
      <p className={s.sub}>
        {present
          ? 'Read it on its check before the letter goes out.'
          : needed === 'proof'
            ? 'Attach it on its check; it counts once the virus check clears it.'
            : 'Typed in on the award or attached on its check.'}
      </p>
    </div>
  );
}

/** The share price the options use, or that the award pays cash only. */
function SharePriceFact({ check }: { check: Check }): ReactElement {
  const changed = check.needs.includes('fmv');
  return (
    <div className={s.rowFact}>
      <p className={cx('eyebrow', s.fieldLabel)}>Share price</p>
      {check.fmvCents === null ? (
        <>
          <p className={s.strong}>Cash only</p>
          <p className={s.sub}>The share price doesn’t apply.</p>
        </>
      ) : (
        <>
          <p className={s.strong}>{formatMoney(check.fmvCents)} per share</p>
          {changed && <StatusPill size="sm" tone="waiting" label="Changed since it was sent" />}
          <p className={s.sub}>
            {changed
              ? 'Open its check to take the values again.'
              : 'Today’s share price · whole options, rounded down'}
          </p>
        </>
      )}
    </div>
  );
}

/**
 * An award's details, opened in place in the queue (APP-109): each person with their amounts,
 * pool and charge codes, what each pool pays, its proof or write-up and its share price. The
 * same facts Mark checked confirms, of the version the row carries, without opening the award.
 */
export function CheckDetails({ row }: { row: QueueRow }): ReactElement | null {
  const check = row.check;
  if (check === null) return null;
  const displayId = row.award.displayId;
  const pools = paidFrom(check.people);
  return (
    <div className={s.rowDetails}>
      <div className={s.rowPeople}>
        <DataTable
          caption={`People on ${displayId}`}
          columns={columns(row, check)}
          rows={check.people}
          rowKey={(p) => p.person.id}
          cards
          empty={{
            title: 'No one on this award yet.',
            body: 'An award reaches the check with at least one person. Open its check to see what happened.',
          }}
        />
      </div>
      <div className={s.rowFacts}>
        <div className={s.rowFact}>
          <p className={cx('eyebrow', s.fieldLabel)}>Paid from</p>
          {pools.length === 0 ? (
            <p className={s.sub}>Nothing to pay until someone is on the award.</p>
          ) : (
            <ul className={s.paidFrom}>
              {pools.map((pool) => (
                <li key={pool.pool.id} className={s.paidRow}>
                  <span>
                    <span className={s.strong}>{pool.pool.name}</span>
                    <span className={s.muted}> · {plural(pool.people, 'person', 'people')}</span>
                  </span>
                  <span className={s.strong}>{amountWords(pool.cashCents, pool.options)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <SharePriceFact check={check} />
        <ProofFact row={row} check={check} />
      </div>
      <Link href={rowHref(row)} className={s.inlineLink}>
        Open the check for {displayId}
      </Link>
    </div>
  );
}
